<template>
  <div class="layout" :class="{'layout--revision': revisionContent}">
    <div class="layout__panel flex flex--row" :class="{'flex--end': styles.showSideBar}">
      <div class="layout__panel layout__panel--explorer" v-show="styles.showExplorer" :aria-hidden="!styles.showExplorer" :style="{width: styles.layoutOverflow ? '100%' : constants.explorerWidth + 'px'}">
        <explorer></explorer>
      </div>
      <div class="layout__panel flex flex--column" tour-step-anchor="welcome,end" :style="{width: styles.innerWidth + 'px'}">
        <div class="layout__panel layout__panel--navigation-bar" v-show="styles.showNavigationBar" :style="{height: constants.navigationBarHeight + 'px'}">
          <navigation-bar></navigation-bar>
        </div>
        <div class="layout__panel flex flex--row" :style="{height: styles.innerHeight + 'px'}">
          <div class="layout__panel layout__panel--editor" v-show="styles.showEditor" :style="{width: styles.editorWidth + 'px', fontSize: styles.fontSize + 'px'}">
            <editor></editor>
          </div>
          <div class="layout__panel layout__panel--button-bar" v-show="styles.showEditor" :style="{width: constants.buttonBarWidth + 'px'}">
            <button-bar></button-bar>
          </div>
          <div class="layout__panel layout__panel--preview" v-show="styles.showPreview" :style="{width: styles.previewWidth + 'px', fontSize: styles.fontSize + 'px'}">
            <preview></preview>
          </div>
          <div class="layout__panel layout__panel--find-replace" v-if="showFindReplace">
            <find-replace></find-replace>
          </div>
        </div>
        <div class="layout__panel layout__panel--status-bar" v-show="styles.showStatusBar" :style="{height: constants.statusBarHeight + 'px'}">
          <status-bar></status-bar>
        </div>
        <mobile-toolbar></mobile-toolbar>
      </div>
      <div class="layout__panel layout__panel--side-bar" v-show="styles.showSideBar" :style="{width: constants.sideBarWidth + 'px'}">
        <side-bar></side-bar>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import NavigationBar from './NavigationBar.vue';
import ButtonBar from './ButtonBar.vue';
import StatusBar from './StatusBar.vue';
import Explorer from './Explorer.vue';
import SideBar from './SideBar.vue';
import Editor from './Editor.vue';
import Preview from './Preview.vue';
import FindReplace from './FindReplace.vue';
import MobileToolbar from './MobileToolbar.vue';
import editorSvc from '../services/editorSvc';
import markdownConversionSvc from '../services/markdownConversionSvc';
import store from '../store';

export default {
  components: {
    NavigationBar,
    ButtonBar,
    StatusBar,
    Explorer,
    SideBar,
    Editor,
    Preview,
    FindReplace,
    MobileToolbar,
  },
  computed: {
    ...mapState([
      'light',
    ]),
    ...mapState('content', [
      'revisionContent',
    ]),
    ...mapGetters('layout', [
      'constants',
      'styles',
    ]),
    ...mapGetters('data', [
      'layoutSettings',
    ]),
    showFindReplace() {
      return !!store.state.findReplace.type;
    },
  },
  methods: {
    ...mapActions('layout', [
      'updateBodySize',
    ]),
    saveSelection: () => editorSvc.saveSelection(true),
  },
  created() {
    markdownConversionSvc.init(); // Needs to be inited before mount
    this.updateBodySize();
    window.addEventListener('resize', this.updateBodySize);
    window.addEventListener('keyup', this.saveSelection);
    window.addEventListener('mouseup', this.saveSelection);
    window.addEventListener('focusin', this.saveSelection);
    window.addEventListener('contextmenu', this.saveSelection);
  },
  mounted() {
    // Use $nextTick to ensure all child components (including SideBar/Toc) are fully rendered
    this.$nextTick(() => {
      const editorElt = this.$el.querySelector('.editor__inner');
      const previewElt = this.$el.querySelector('.preview__inner-2');
      const tocElt = this.$el.querySelector('.toc__inner');
      editorSvc.init(editorElt, previewElt, tocElt);

      // Focus on the editor every time reader mode is disabled
      const focus = () => {
        if (this.styles.showEditor) {
          editorSvc.clEditor.focus();
        }
      };
      setTimeout(focus, 100);
      this.$watch(() => this.styles.showEditor, focus);
    });
  },
  destroyed() {
    window.removeEventListener('resize', this.updateStyle);
    window.removeEventListener('keyup', this.saveSelection);
    window.removeEventListener('mouseup', this.saveSelection);
    window.removeEventListener('focusin', this.saveSelection);
    window.removeEventListener('contextmenu', this.saveSelection);
  },
};
</script>

<style lang="scss">
@import '../styles/variables.scss';

.layout {
  position: absolute;
  width: 100%;
  height: 100%;
}

.layout__panel {
  position: relative;
  width: 100%;
  height: 100%;
  flex: none;
  overflow: hidden;
}

.layout__panel--navigation-bar {
  background-color: $navbar-bg;
  
  @media (max-width: 768px) {
    overflow: visible;
  }
}

.layout__panel--status-bar {
  background-color: #007acc;

  .app--light & {
    background-color: #e0e0e0;
  }

  .app--dark & {
    background-color: #1a1a2e;
  }

  .app--sepia & {
    background-color: #d4c4a8;
  }

  .app--nord & {
    background-color: #3b4252;
  }

  .app--solarized-light & {
    background-color: #eee8d5;
  }

  .app--solarized-dark & {
    background-color: #073642;
  }

  .app--dracula & {
    background-color: #44475a;
  }

  .app--monokai & {
    background-color: #3e3d32;
  }
}

.layout__panel--editor {
  background-color: $editor-background-light;

  .app--dark & {
    background-color: $editor-background-dark;
    color: rgba(255, 255, 255, 0.87);
  }

  .app--sepia & {
    background-color: #f4ecd8;
    color: #5b4636;
  }

  .app--nord & {
    background-color: #2e3440;
    color: #d8dee9;
  }

  .app--solarized-light & {
    background-color: #fdf6e3;
    color: #657b83;
  }

  .app--solarized-dark & {
    background-color: #002b36;
    color: #93a1a1;
  }

  .app--dracula & {
    background-color: #282a36;
    color: #f8f8f2;
  }

  .app--monokai & {
    background-color: #272822;
    color: #f8f8f2;
  }

  .gutter__background,
  .comment-list__current-discussion,
  .sticky-comment,
  .current-discussion {
    background-color: mix(#000, $editor-background-light, 6.7%);

    .app--dark & {
      background-color: mix(#fff, $editor-background-dark, 6.7%);
    }
  }
}

$preview-background-light: #f3f3f3;
$preview-background-dark: #252525;

.layout__panel--preview,
.layout__panel--button-bar {
  background-color: $preview-background-light;

  .app--dark & {
    background-color: $preview-background-dark;
  }

  .app--sepia & {
    background-color: #f4ecd8;
  }

  .app--nord & {
    background-color: #2e3440;
  }

  .app--solarized-light & {
    background-color: #fdf6e3;
  }

  .app--solarized-dark & {
    background-color: #002b36;
  }

  .app--dracula & {
    background-color: #282a36;
  }

  .app--monokai & {
    background-color: #272822;
  }

  overflow: visible;
}

.layout__panel--preview {
  .gutter__background,
  .comment-list__current-discussion,
  .sticky-comment,
  .current-discussion {
    background-color: mix(#000, $preview-background-light, 6.7%);
  }
}

.layout__panel--button-bar {
  overflow: visible;
}

// At very small screens, button bar becomes absolute overlay on right
@media (max-width: 360px) {
  .layout__panel--button-bar {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 36px;
    z-index: 2;
  }
}


.layout__panel--explorer,
.layout__panel--side-bar {
  background-color: #ddd;
  color: rgba(0, 0, 0, 0.87);

  .app--dark & {
    background-color: #2d2d2d;
    color: rgba(255, 255, 255, 0.87);
  }

  .app--sepia & {
    background-color: #e8dcc8;
    color: #5b4636;
  }

  .app--nord & {
    background-color: #3b4252;
    color: #d8dee9;
  }

  .app--solarized-light & {
    background-color: #eee8d5;
    color: #657b83;
  }

  .app--solarized-dark & {
    background-color: #073642;
    color: #93a1a1;
  }

  .app--dracula & {
    background-color: #21222c;
    color: #f8f8f2;
  }

  .app--monokai & {
    background-color: #1e1f1c;
    color: #f8f8f2;
  }
}

.layout__panel--find-replace {
  background-color: #e6e6e6;
  position: absolute;
  left: 0;
  bottom: 0;
  width: 300px;
  height: auto;
  border-top-right-radius: $border-radius-base;

  .app--dark & {
    background-color: #3d3d3d;
  }

  .app--sepia & {
    background-color: #e8dcc8;
  }

  .app--nord & {
    background-color: #3b4252;
  }

  .app--solarized-light & {
    background-color: #eee8d5;
  }

  .app--solarized-dark & {
    background-color: #073642;
  }

  .app--dracula & {
    background-color: #21222c;
  }

  .app--monokai & {
    background-color: #1e1f1c;
  }
}

// Responsive - Explorer and Sidebar overlay on smaller screens
@media (max-width: 900px) {
  .layout__panel--explorer,
  .layout__panel--side-bar {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: 10;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);
  }

  .layout__panel--explorer {
    left: 0;
  }

  .layout__panel--side-bar {
    right: 0;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
  }
}

@media (max-width: 600px) {
  .layout__panel--find-replace {
    width: 100%;
    border-top-right-radius: 0;
  }
}
</style>
